import { inject, Injectable } from '@angular/core';
import { LanguageService } from '@uigy/shared/services/language.service';
import {
  AssetCategory,
  AssetExpressionsParams,
  AssetField,
  ExprOp,
  FindingField,
  FindingsObjExpressionsParams,
  PluginSeverity,
} from '@uigy/generated/graphql';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { MainKpiService } from '@uigy/modules/overview/kpis/kpi-main/services/main-kpi.service';
import { MainKpiConfig } from '@uigy/modules/overview/kpis/kpi.interfaces';
import { RISK_MEDIUM_LIMIT } from '@uigy/shared/services/risk.service';
import { VulnerabilityFindingsService } from '@uigy/modules/findings/components/findings-vulnerabilities/services/vulnerability-findings.service';
import { GridUtils } from '@uigy/shared/components/ui-grid/grid-utils';
import { UiGridFilterModel } from '@uigy/shared/components/ui-grid/models/ui-grid';
import { NavigationRoutesService } from '@uigy/core/modules/layout/navigation-routes.service';
import {
  FindingsTabQueryParams,
  InventoryTabQueryParams,
  TabGroup,
  TabQueryParams,
} from '@uigy/shared/components/tabs/tabs.enums';
import { InventoryUiGridService } from '@uigy/modules/inventory/services/inventory-ui-grid.service';

@Injectable({
  providedIn: 'root'
})
export class MainKpiICPConfigService {
  private languageService = inject(LanguageService);
  private mainKpiService = inject(MainKpiService);
  private navigationRoutesService = inject(NavigationRoutesService);

  private readonly texts = this.languageService.texts.overview.kpi_widgets;

  private readonly ControllersCategoryConfig: MainKpiConfig<UiGridFilterModel<AssetField>> = {
    filter: this.baseAssetFilter(AssetCategory.ControllersCategory),
    riskFilter: this.baseAssetFilter(AssetCategory.ControllersCategory, true),
    fetchData: (filters) => this.mainKpiService.fetchCombinedKpiCount<AssetExpressionsParams>(filters, this.mainKpiService.getAssetsCount),
    title: this.texts.ot_controllers.title,
    subTitle: this.texts.sub_title,
    riskScoreTooltip: this.texts.risk_score_tooltip,
    callbackFn: () =>
      this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.inventory], {
        [TabGroup.InventoryTab]: InventoryTabQueryParams.ControllersAssets,
        groupBy: AssetField.BackplaneName,
        sort: encodeURI(JSON.stringify(InventoryUiGridService.defaultSort)),
      })
  };

  private readonly IotCategoryConfig: MainKpiConfig<UiGridFilterModel<AssetField>> = {
    filter: this.baseAssetFilter(AssetCategory.IotCategory),
    riskFilter: this.baseAssetFilter(AssetCategory.IotCategory, true),
    fetchData: filters => this.mainKpiService.fetchCombinedKpiCount<AssetExpressionsParams>(filters, this.mainKpiService.getAssetsCount),
    title: this.texts.iot_assets.title,
    subTitle: this.texts.sub_title,
    riskScoreTooltip: this.texts.risk_score_tooltip,
    callbackFn: () =>
      this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.inventory], {
        [TabGroup.InventoryTab]: InventoryTabQueryParams.IotAssets, sort: encodeURI(JSON.stringify(InventoryUiGridService.defaultSort)),
      })
  };

  private readonly NetworkAssetsCategoryConfig: MainKpiConfig<UiGridFilterModel<AssetField>> = {
    filter: this.baseAssetFilter(AssetCategory.NetworkAssetsCategory),
    riskFilter: this.baseAssetFilter(AssetCategory.NetworkAssetsCategory, true),
    fetchData: filters => this.mainKpiService.fetchCombinedKpiCount<AssetExpressionsParams>(filters, this.mainKpiService.getAssetsCount),
    title: this.texts.network_assets.title,
    subTitle: this.texts.sub_title,
    riskScoreTooltip: this.texts.risk_score_tooltip,
    callbackFn: () =>
      this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.inventory], {
        [TabGroup.InventoryTab]: InventoryTabQueryParams.NetworkAssets, sort: encodeURI(JSON.stringify(InventoryUiGridService.defaultSort)),
      })
  };

  private readonly VulnerabilityFindingsConfig: MainKpiConfig<UiGridFilterModel<FindingField>> = {
    filter: this.baseFindingFilter(),
    riskFilter: this.baseFindingFilter(true),
    fetchData: filters => this.mainKpiService.fetchCombinedKpiCount<FindingsObjExpressionsParams>(filters, this.mainKpiService.getFindingsCount),
    title: this.texts.vulnerability_finding.title,
    subTitle: this.texts.vulnerability_finding.sub_title,
    riskScoreTooltip: this.texts.vulnerability_finding.tooltip,
    callbackFn: () =>
      this.navigationRoutesService.navigateWithParams([RoutesStatesNamesEnum.risks, RoutesStatesNamesEnum.findings], {
        [TabGroup.FindingsTab]: FindingsTabQueryParams.Vulnerabilities,
        ...VulnerabilityFindingsService.baseURLParams
      }),
  };

  constructor() {
  }

  public getKpiConfigs(): (MainKpiConfig<UiGridFilterModel<AssetField>> | MainKpiConfig<UiGridFilterModel<FindingField>>)[] {
    return [
      this.ControllersCategoryConfig,
      this.IotCategoryConfig,
      this.NetworkAssetsCategoryConfig,
      this.VulnerabilityFindingsConfig,
    ];
  }

  private baseAssetFilter(category: AssetCategory, riskCondition: boolean = false): UiGridFilterModel<AssetField> {
    const expressions: UiGridFilterModel<AssetField>[] = [
      {field: AssetField.Category, op: ExprOp.Equal, values: category},
      {field: AssetField.Hidden, op: ExprOp.Equal, values: false},
    ];

    if (riskCondition) {
      expressions.push({field: AssetField.Risk, op: ExprOp.GreaterEqual, values: RISK_MEDIUM_LIMIT});
    }

    return {
      op: ExprOp.And,
      expressions,
    };
  }

  private baseFindingFilter(riskCondition: boolean = false): UiGridFilterModel<FindingField> {
    const baseFilter = VulnerabilityFindingsService.baseFilter;

    if (riskCondition) {
      return GridUtils.combineFilters({
        field: FindingField.PluginSeverity,
        op: ExprOp.In,
        values: [PluginSeverity.High, PluginSeverity.Critical]
      }, baseFilter);
    }

    return baseFilter;
  }
}



